import { useState, useEffect, useCallback } from "react";
import {
  HiOutlinePlus, HiOutlineTrash,
  HiOutlinePencil, HiOutlineCheck, HiOutlineXMark,
  HiOutlineUser, HiOutlineKey, HiOutlineEye, HiOutlineEyeSlash,
} from "react-icons/hi2";
import AdminLayout from "./AdminLayout";
import { getToken } from "../services/api";
import toast from "react-hot-toast";
import "./AdminRoles.css";
import { useCurrentAdmin, hasFeature } from "./permissions";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const apiFetch = (path, opts = {}) =>
  fetch(`${API}${path}`, {
    headers: { "Content-Type":"application/json", Authorization:`Bearer ${getToken()}`, ...opts.headers },
    ...opts,
  }).then(r => r.json());
/** Like apiFetch, but a refusal is an error (so "Role deleted." never shows when it wasn't). */
const apiDo = (path, opts) => apiFetch(path, opts).then((res) => { if (!res.success) throw new Error(res.message || "That didn't work."); return res; });

/* ── Permission groups ── */
const SECTION_LABELS = {
  "applications.view":               "Applications — View",
  "applications.update_status":      "Applications — Update Status",
  "applications.add_notes":          "Applications — Add Notes",
  "applications.pipeline_clearance": "Applications — Pipeline Clearance",
  "courses.view":    "Courses — View",
  "courses.edit":    "Courses — Edit",
  "courses.publish": "Courses — Publish",
  "donations.view":  "Donations — View",
  "donations.refund":"Donations — Refund",
  "stories.view":     "Stories — View",
  "stories.moderate": "Stories — Moderate",
  "subscribers.view":      "Subscribers — View",
  "subscribers.broadcast": "Subscribers — Broadcast",
  "settings.view": "Settings — View",
  "settings.edit": "Settings — Edit",
  "notifications.view": "Notifications — View",
  "workshops.view": "Workshops — View",
  "workshops.edit": "Workshops — Create & Edit",
  "shop.view": "Shop — View products & orders",
  "shop.edit": "Shop — Edit products & orders",
  "admins.view": "Admins — View",
  "admins.edit": "Admins — Edit",
  "roles.manage": "Roles — Manage (Super Admin only)",
  "queue.view": "Queue — View",
  "queue.manage": "Queue — Manage offers",
  "blocks.view": "Retreats — View",
  "blocks.edit": "Retreats — Edit dates, prices and add-ons",
  "cohorts.view": "Cohorts — View",
  "cohorts.manage": "Cohorts — Manage",
  "board.view": "Message board — View",
  "board.moderate": "Message board — Moderate",
  "resources.view": "Resources — View",
  "resources.edit": "Resources — Edit",
  "reservations.view": "Reservations — View",
  "reservations.manage": "Reservations — Book, change, move, cancel",
  "payments.record": "Payments — Record payments and refunds",
  "screening.update": "Medical screening — Update status",
  "inventory.view": "Rooms — View calendar and accommodation",
  "inventory.edit": "Rooms — Edit accommodation and blocks",
  "kitchen.view": "Kitchen — View daily sheet",
  "kitchen.edit": "Kitchen — Edit menus",
  "sellers.view": "Sellers — View",
  "sellers.edit": "Sellers — Add and edit",
  "clinical.view": "Clinical records — Open medical documents (Super Admins need this too)",
};

/* `feature` hides a group on the product that doesn't have those screens (config/product.js). */
const GROUPS = [
  { label:"Applications",  keys:["applications.view","applications.update_status","applications.add_notes","applications.pipeline_clearance"], feature:"applications" },
  { label:"Queue",         keys:["queue.view","queue.manage"], feature:"queue" },
  { label:"Retreats",      keys:["blocks.view","blocks.edit"] },
  { label:"Cohorts",       keys:["cohorts.view","cohorts.manage"], feature:"cohorts" },
  { label:"Reservations",  keys:["reservations.view","reservations.manage","payments.record","screening.update"], feature:"reservations" },
  { label:"Rooms",         keys:["inventory.view","inventory.edit"], feature:"reservations" },
  { label:"Kitchen",       keys:["kitchen.view","kitchen.edit"], feature:"reservations" },
  { label:"Sellers",       keys:["sellers.view","sellers.edit"], feature:"reservations" },
  { label:"Courses",       keys:["courses.view","courses.edit","courses.publish"] },
  { label:"Message board", keys:["board.view","board.moderate"], feature:"community" },
  { label:"Resources",     keys:["resources.view","resources.edit"], feature:"resources" },
  { label:"Donations",     keys:["donations.view","donations.refund"], feature:"donations" },
  { label:"Stories",       keys:["stories.view","stories.moderate"], feature:"stories" },
  { label:"Subscribers",   keys:["subscribers.view","subscribers.broadcast"] },
  { label:"Settings",      keys:["settings.view","settings.edit"] },
  { label:"Workshops",     keys:["workshops.view","workshops.edit"], feature:"workshops" },
  { label:"Shop",          keys:["shop.view","shop.edit"], feature:"shop" },
  { label:"Notifications", keys:["notifications.view"] },
  { label:"Admin & Roles", keys:["admins.view","admins.edit","roles.manage"] },
  { label:"Clinical",      keys:["clinical.view"] },
];

function PermissionEditor({ permissions, onChange }) {
  const { admin } = useCurrentAdmin();
  const groups = GROUPS.filter((g) => hasFeature(admin, g.feature));
  const toggle = (key) => onChange({ ...permissions, [key]: !permissions[key] });
  const toggleGroup = (keys) => {
    const allOn = keys.every(k => permissions[k]);
    onChange({ ...permissions, ...Object.fromEntries(keys.map(k => [k, !allOn])) });
  };
  return (
    <div className="ar__perm-grid">
      {groups.map(g => (
        <div key={g.label} className="ar__perm-group">
          <div className="ar__perm-group-head">
            <button type="button" className="ar__perm-group-toggle" onClick={() => toggleGroup(g.keys)}>
              {g.keys.every(k => permissions[k]) ? <HiOutlineCheck style={{fontSize:"0.7rem",color:"#16a34a"}}/> : <span/>}
            </button>
            <span className="ar__perm-group-label">{g.label}</span>
          </div>
          {g.keys.map(k => (
            <label key={k} className="ar__perm-item">
              <input type="checkbox" checked={!!permissions[k]} onChange={() => toggle(k)} className="ar__perm-checkbox"/>
              <span>{SECTION_LABELS[k] || k}</span>
            </label>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ── Create Admin form ── */
function CreateAdminForm({ roles, onCreated, onCancel }) {
  const [form, setForm] = useState({ name:"", email:"", password:"", role:"admin", role_id:"" });
  const [showPw, setShowPw]   = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error,  setError]    = useState("");

  const handleSave = async () => {
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.password)
      { setError("Name, email and password are required."); return; }
    if (form.password.length < 8)
      { setError("Password must be at least 8 characters."); return; }
    if (form.role !== "super_admin" && !form.role_id)
      { setError("A permission role is required for this admin."); return; }
    setSaving(true);
    try {
      const res = await apiFetch("/admin/roles/admins/create", {
        method: "POST",
        body: JSON.stringify({ ...form, role_id: form.role_id || null }),
      });
      if (!res.success) { setError(res.message || "Failed."); return; }
      toast.success("Admin account created.");
      onCreated();
    } catch (err) { setError(err.message || "Failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="acard ar__edit-panel">
      <div className="ar__edit-head">
        <p className="ar__edit-title">Create Admin Account</p>
        <button type="button" className="acr__icon-btn" onClick={onCancel}><HiOutlineXMark/></button>
      </div>
      <div className="ar__edit-body">
        <div className="ar__create-grid">
          <div className="ar__form-field">
            <label className="ar__form-label">Full Name</label>
            <input className="ar__form-input" placeholder="e.g. Sarah Johnson"
              value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))}/>
          </div>
          <div className="ar__form-field">
            <label className="ar__form-label">Email Address</label>
            <input className="ar__form-input" type="email" placeholder="sarah@forge.ngo"
              value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))}/>
          </div>
          <div className="ar__form-field">
            <label className="ar__form-label">Password</label>
            <div style={{position:"relative"}}>
              <input className="ar__form-input" type={showPw?"text":"password"}
                placeholder="Min 8 characters" style={{paddingRight:"2.5rem"}}
                value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))}/>
              <button type="button" onClick={()=>setShowPw(v=>!v)}
                style={{position:"absolute",right:"0.65rem",top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"#6b6258",fontSize:"1rem"}}>
                {showPw?<HiOutlineEyeSlash/>:<HiOutlineEye/>}
              </button>
            </div>
          </div>
          <div className="ar__form-field">
            <label className="ar__form-label">System Role</label>
            <select className="ar__form-input ar__form-select"
              value={form.role} onChange={e=>setForm(p=>({...p,role:e.target.value}))}>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
          <div className="ar__form-field" style={{gridColumn:"1/-1"}}>
            <label className="ar__form-label">
              Permission Role {form.role !== "super_admin" && <span style={{color:"#dc2626"}}>*</span>}
            </label>
            {form.role === "super_admin" ? (
              <p style={{fontSize:"0.82rem",color:"#6b6258",margin:"0.3rem 0 0"}}>
                Super admins have full access automatically — no permission role needed.
              </p>
            ) : (
              <>
                <select className="ar__form-input ar__form-select"
                  value={form.role_id} onChange={e=>setForm(p=>({...p,role_id:e.target.value}))}>
                  <option value="">Select a permission role…</option>
                  {roles.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <p className="ar__form-hint">
                  Required. Defines exactly what this admin can see and do.
                  {roles.length === 0 && " No roles exist yet — create one first in Permission Roles."}
                </p>
              </>
            )}
          </div>
        </div>
        {error && <p style={{fontSize:"0.82rem",fontWeight:600,color:"#dc2626",marginTop:"0.5rem"}}>{error}</p>}
      </div>
      <div className="ar__edit-footer">
        <button type="button" className="a-btn a-btn--outline" onClick={onCancel}>Cancel</button>
        <button type="button" className="a-btn a-btn--rust" onClick={handleSave} disabled={saving}>
          {saving ? "Creating…" : "Create Account"}
        </button>
      </div>
    </div>
  );
}

/* ── Role card ── */
function RoleCard({ role, onEdit, onDelete }) {
  const perms = typeof role.permissions === "string" ? JSON.parse(role.permissions||"{}") : (role.permissions||{});
  const permCount = Object.values(perms).filter(Boolean).length;
  return (
    <div className="acard ar__role-card">
      <div className="ar__role-head">
        <div className="ar__role-icon"><HiOutlineKey/></div>
        <div className="ar__role-info">
          <p className="ar__role-name">{role.name}</p>
          <p className="ar__role-meta">{permCount} permission{permCount!==1?"s":""} · {role.admin_count||0} admin{role.admin_count!==1?"s":""}</p>
        </div>
        <div className="ar__role-actions">
          <button type="button" className="a-btn a-btn--outline" style={{fontSize:"0.78rem"}} onClick={()=>onEdit(role)}>
            <HiOutlinePencil/> Edit
          </button>
          <button type="button" className="a-btn a-btn--outline ar__delete-btn" aria-label={`Delete the ${role.name} role`} onClick={()=>onDelete(role.id)}>
            <HiOutlineTrash/>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main page ── */
export default function AdminRoles() {
  const [roles,   setRoles]   = useState([]);
  const [admins,  setAdmins]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState("roles");

  const [editing,   setEditing]   = useState(null);
  const [formName,  setFormName]  = useState("");
  const [formPerms, setFormPerms] = useState({});
  const [saving,    setSaving]    = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        apiFetch("/admin/roles"),
        apiFetch("/admin/roles/admins"),
      ]);
      setRoles((r.data||[]).map(role => ({
        ...role,
        permissions: typeof role.permissions === "string" ? JSON.parse(role.permissions||"{}") : (role.permissions||{})
      })));
      setAdmins((a.data||[]).map(admin => ({
        ...admin,
        permissions: typeof admin.permissions === "string" ? JSON.parse(admin.permissions||"{}") : (admin.permissions||{})
      })));
    } catch (err) { console.error(err); toast.error("Failed to load roles."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew  = () => { setEditing("new"); setFormName(""); setFormPerms({}); };
  const openEdit = (role) => {
    setEditing(role);
    setFormName(role.name);
    setFormPerms(typeof role.permissions === "string" ? JSON.parse(role.permissions||"{}") : (role.permissions||{}));
  };
  const closeEdit = () => setEditing(null);

  const handleSave = async () => {
    if (!formName.trim()) { toast.error("Role name is required."); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        await apiDo("/admin/roles", { method:"POST", body:JSON.stringify({ name:formName, permissions:formPerms }) });
        toast.success("Role created.");
      } else {
        await apiDo(`/admin/roles/${editing.id}`, { method:"PUT", body:JSON.stringify({ name:formName, permissions:formPerms }) });
        toast.success("Role updated.");
      }
      await fetchAll();
      closeEdit();
    } catch (err) { toast.error(err.message || "Failed."); }
    finally { setSaving(false); }
  };

  const handleDelete = (id) => {
    toast((t) => (
      <div style={{display:"flex",flexDirection:"column",gap:"0.5rem"}}>
        <p style={{margin:0,fontWeight:700}}>Delete this role?</p>
        <p style={{margin:0,fontSize:"0.78rem",color:"#6b6258"}}>Only a role nobody has can be deleted. Give its staff another role first.</p>
        <div style={{display:"flex",gap:"0.5rem"}}>
          <button onClick={()=>{toast.dismiss(t.id); doDelete(id);}}
            style={{background:"#dc2626",color:"#fff",border:"none",borderRadius:"5px",padding:"0.35rem 0.75rem",cursor:"pointer",fontWeight:700,fontSize:"0.78rem"}}>Delete</button>
          <button onClick={()=>toast.dismiss(t.id)}
            style={{background:"#f3f4f0",border:"none",borderRadius:"5px",padding:"0.35rem 0.75rem",cursor:"pointer",fontSize:"0.78rem"}}>Cancel</button>
        </div>
      </div>
    ), { duration:Infinity, style:{maxWidth:"300px"} });
  };

  const doDelete = async (id) => {
    try {
      await apiDo(`/admin/roles/${id}`, { method:"DELETE" });
      toast.success("Role deleted.");
      fetchAll();
    } catch (err) { toast.error(err.message||"Failed."); }
  };

  const handleAssignRole = async (adminId, roleId) => {
    try {
      await apiDo(`/admin/roles/admins/${adminId}`, { method:"PUT", body:JSON.stringify({ role_id: roleId||null }) });
      toast.success("Role changed. It applies from their next click.");
      fetchAll();
    } catch (err) { toast.error(err.message || "Failed to assign role."); }
  };

  /* Switch an account off (signs them out everywhere) or back on. */
  const handleActive = async (a, on) => {
    if (!on && !window.confirm(`Switch off ${a.name}? They're signed out at once and can't sign in until switched back on. Their history stays.`)) return;
    try { const r = await apiDo(`/admin/roles/admins/${a.id}/active`, { method:"PUT", body:JSON.stringify({ active: on }) }); toast.success(r.message); fetchAll(); }
    catch (err) { toast.error(err.message); }
  };
  const handleSignOut = async (a) => {
    if (!window.confirm(`Sign ${a.name} out on every device? They can sign straight back in with their password.`)) return;
    try { const r = await apiDo(`/admin/roles/admins/${a.id}/sign-out`, { method:"POST" }); toast.success(r.message); }
    catch (err) { toast.error(err.message); }
  };
  const [editingAdmin, setEditingAdmin] = useState(null);
  const saveDetails = async (f) => {
    try { const r = await apiDo(`/admin/roles/admins/${editingAdmin.id}/details`, { method:"PUT", body:JSON.stringify(f) }); toast.success(r.message); setEditingAdmin(null); fetchAll(); }
    catch (err) { toast.error(err.message); }
  };

  /* Someone lost their phone: clear their two-step so they can sign in with their password and set it up again. */
  const handleResetTwoStep = async (a) => {
    if (!window.confirm(`Reset two-step sign-in for ${a.name}? They'll sign in with just their password until they set it up again.`)) return;
    const res = await apiFetch(`/auth/mfa/reset/${a.id}`, { method: "POST" });
    if (res.success) { toast.success(res.message); fetchAll(); } else toast.error(res.message || "Couldn't reset.");
  };

  const handleDeleteAdmin = (admin) => {
    toast((t) => (
      <div style={{display:"flex",flexDirection:"column",gap:"0.5rem"}}>
        <p style={{margin:0,fontWeight:700}}>Delete {admin.name}?</p>
        <p style={{margin:0,fontSize:"0.78rem",color:"#6b6258"}}>They lose access immediately. Someone with changes on record is switched off instead, so the history keeps their name.</p>
        <div style={{display:"flex",gap:"0.5rem"}}>
          <button onClick={()=>{toast.dismiss(t.id); doDeleteAdmin(admin.id);}}
            style={{background:"#1b1b1b",color:"#fff",border:"none",borderRadius:"5px",padding:"0.35rem 0.75rem",cursor:"pointer",fontWeight:700,fontSize:"0.78rem"}}>Delete Account</button>
          <button onClick={()=>toast.dismiss(t.id)}
            style={{background:"#f3f4f0",border:"none",borderRadius:"5px",padding:"0.35rem 0.75rem",cursor:"pointer",fontSize:"0.78rem"}}>Cancel</button>
        </div>
      </div>
    ), { duration:Infinity, style:{maxWidth:"320px"} });
  };

  const doDeleteAdmin = async (id) => {
    try {
      const res = await apiFetch(`/admin/roles/admins/${id}`, { method:"DELETE" });
      if (!res.success) { toast.error(res.message||"Failed."); return; }
      toast.success(res.message || "Admin account deleted.");
      fetchAll();
    } catch (err) { toast.error(err.message||"Failed."); }
  };

  return (
    <AdminLayout pageTitle="Staff & Permissions" pageSubtitle="Staff accounts on this site, and the roles that decide what each can see and do." permission="SUPER_ADMIN_ONLY">
      {/* Tabs + actions */}
      <div className="ar__tabs">
        <button type="button" className={`ar__tab ${tab==="roles"?"ar__tab--active":""}`} onClick={()=>setTab("roles")}>
          <HiOutlineKey/> Permission Roles ({roles.length})
        </button>
        <button type="button" className={`ar__tab ${tab==="admins"?"ar__tab--active":""}`} onClick={()=>setTab("admins")}>
          <HiOutlineUser/> Staff Accounts ({admins.length})
        </button>
        <div style={{marginLeft:"auto",display:"flex",gap:"0.5rem"}}>
          {tab==="roles" && !editing && (
            <button type="button" className="a-btn a-btn--outline" onClick={openNew}>
              <HiOutlinePlus/> New Role
            </button>
          )}
          {tab==="admins" && !showCreate && (
            <button type="button" className="a-btn a-btn--rust" onClick={()=>setShowCreate(true)}>
              <HiOutlinePlus/> Add Staff
            </button>
          )}
        </div>
      </div>

      {/* ── ROLES TAB ── */}
      {tab==="roles" && (
        <>
          {/* Edit / Create role form */}
          {editing && (
            <div className="acard ar__edit-panel">
              <div className="ar__edit-head">
                <p className="ar__edit-title">{editing==="new" ? "Create New Role" : `Edit: ${editing.name}`}</p>
                <button type="button" className="acr__icon-btn" onClick={closeEdit}><HiOutlineXMark/></button>
              </div>
              <div className="ar__edit-body">
                <div className="ar__form-field">
                  <label className="ar__form-label">Role Name</label>
                  <input className="ar__form-input" value={formName}
                    onChange={e=>setFormName(e.target.value)} placeholder="e.g. Case Manager, Medical Reviewer…"/>
                </div>
                <div className="ar__form-field">
                  <label className="ar__form-label">Permissions</label>
                  <p className="ar__form-hint">Click a section header to toggle all permissions in that section at once.</p>
                  <PermissionEditor permissions={formPerms} onChange={setFormPerms}/>
                </div>
              </div>
              <div className="ar__edit-footer">
                <button type="button" className="a-btn a-btn--outline" onClick={closeEdit}>Cancel</button>
                <button type="button" className="a-btn a-btn--rust" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving…" : editing==="new" ? "Create Role" : "Save Changes"}
                </button>
              </div>
            </div>
          )}
          {/* Roles list */}
          {!editing && (
            loading ? <div className="ar__loading"><div className="acr__spinner"/></div>
            : roles.length===0 ? (
              <div className="acard ar__empty">
                <HiOutlineKey className="ar__empty-icon"/>
                <p className="ar__empty-title">No custom roles yet</p>
                <p className="ar__empty-sub">Create a role to define what different admin staff can access.</p>
              </div>
            ) : (
              <div className="ar__role-list">
                {roles.map(role => <RoleCard key={role.id} role={role} onEdit={openEdit} onDelete={handleDelete}/>)}
              </div>
            )
          )}
        </>
      )}

      {/* ── ADMINS TAB ── */}
      {tab==="admins" && (
        <>
          {showCreate && (
            <CreateAdminForm roles={roles} onCancel={()=>setShowCreate(false)}
              onCreated={() => { setShowCreate(false); fetchAll(); }}/>
          )}
          {!showCreate && (
            <div className="acard ar__admins-table">
              <table className="aap__table" style={{width:"100%"}}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>System Role</th>
                    <th>Permission Role</th>
                    <th>Status</th>
                    <th>Last sign-in</th>
                    <th>Two-step</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="7" style={{textAlign:"center",padding:"2rem",color:"#9ca3af"}}>Loading…</td></tr>
                  ) : admins.length===0 ? (
                    <tr><td colSpan="7" style={{textAlign:"center",padding:"2rem",color:"#9ca3af"}}>No admins found.</td></tr>
                  ) : admins.map(a => (
                    <tr key={a.id} style={a.active === 0 ? { opacity: 0.6 } : null}>
                      <td><span style={{fontWeight:600}}>{a.name}</span><br/><span style={{color:"#6b6258",fontSize:"0.8rem"}}>{a.email}</span></td>
                      <td>
                        <span style={{
                          background: a.role==="super_admin" ? "rgba(184,92,56,0.1)" : "rgba(107,98,88,0.1)",
                          color: a.role==="super_admin" ? "#b85c38" : "#6b6258",
                          fontSize:"0.68rem",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",
                          padding:"0.2rem 0.55rem",borderRadius:"99px",
                        }}>
                          {a.role==="super_admin" ? "Super Admin" : "Admin"}
                        </span>
                      </td>
                      <td>
                        {a.role==="super_admin" ? (
                          <>
                            <span style={{fontSize:"0.82rem",color:"#6b6258"}}>Full access</span>
                            {/* Clinical records aren't part of full access: a super admin needs a role that grants them. */}
                            <select className="ar__form-input" aria-label={`Extra role for ${a.name}`} style={{padding:"0.3rem 0.5rem",fontSize:"0.78rem",maxWidth:"180px",marginTop:"0.3rem",display:"block"}}
                              value={a.role_id||""} onChange={e => handleAssignRole(a.id, e.target.value)}>
                              <option value="">No clinical access</option>
                              {roles.filter(r => r.permissions?.["clinical.view"]).map(r => <option key={r.id} value={r.id}>Clinical: {r.name}</option>)}
                            </select>
                          </>
                        ) : (
                          <>
                            <select className="ar__form-input" style={{padding:"0.35rem 0.6rem",fontSize:"0.82rem",maxWidth:"180px",
                              borderColor: !a.role_id ? "#dc2626" : undefined}}
                              value={a.role_id||""}
                              onChange={e => {
                                if (!e.target.value) { toast.error("A permission role is required — choose one below."); return; }
                                handleAssignRole(a.id, e.target.value);
                              }}>
                              <option value="">⚠ No role assigned</option>
                              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </select>
                            {!a.role_id && <p style={{fontSize:"0.68rem",color:"#dc2626",margin:"0.25rem 0 0"}}>Needs a role assigned</p>}
                          </>
                        )}
                      </td>
                      <td style={{fontSize:"0.8rem"}}>
                        {a.active === 0 ? <span style={{color:"#b91c1c",fontWeight:700}}>Switched off</span> : <span style={{color:"#16a34a",fontWeight:700}}>Active</span>}
                      </td>
                      <td style={{fontSize:"0.8rem",color:"#6b6258"}}>
                        {a.last_login_at ? new Date(a.last_login_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "Never"}
                        <br/><span style={{fontSize:"0.72rem"}}>joined {a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</span>
                      </td>
                      <td style={{fontSize:"0.8rem"}}>
                        {a.mfa_enabled_at ? (
                          <span style={{display:"inline-flex",gap:"0.4rem",alignItems:"center",flexWrap:"wrap"}}>
                            <span style={{color:"#16a34a",fontWeight:700}}>On</span>
                            <button type="button" className="a-btn a-btn--ghost" style={{fontSize:"0.76rem",padding:"0.2rem 0.5rem"}}
                              onClick={() => handleResetTwoStep(a)}>Reset</button>
                          </span>
                        ) : <span style={{color:"#6b6258"}}>Off</span>}
                      </td>
                      <td>
                        <span style={{display:"inline-flex",gap:"0.3rem",flexWrap:"wrap",justifyContent:"flex-end"}}>
                          <button type="button" className="a-btn a-btn--ghost" style={{fontSize:"0.76rem",padding:"0.2rem 0.5rem"}} onClick={() => setEditingAdmin(a)}>Edit</button>
                          {a.active === 0
                            ? <button type="button" className="a-btn a-btn--ghost" style={{fontSize:"0.76rem",padding:"0.2rem 0.5rem"}} onClick={() => handleActive(a, true)}>Switch on</button>
                            : <>
                                <button type="button" className="a-btn a-btn--ghost" style={{fontSize:"0.76rem",padding:"0.2rem 0.5rem"}} onClick={() => handleSignOut(a)}>Sign out everywhere</button>
                                <button type="button" className="a-btn a-btn--ghost" style={{fontSize:"0.76rem",padding:"0.2rem 0.5rem"}} onClick={() => handleActive(a, false)}>Switch off</button>
                              </>}
                          {a.role!=="super_admin" && (
                            <button type="button" className="a-btn a-btn--ghost ar__delete-btn" aria-label={`Delete ${a.name}`}
                              style={{fontSize:"0.8rem"}} onClick={()=>handleDeleteAdmin(a)}>
                              <HiOutlineTrash/>
                            </button>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {editingAdmin && <EditStaff a={editingAdmin} onCancel={() => setEditingAdmin(null)} onSave={saveDetails} />}
    </AdminLayout>
  );
}

/** A staff member's name, email and (optionally) a new password. */
function EditStaff({ a, onCancel, onSave }) {
  const [f, setF] = useState({ name: a.name || "", email: a.email || "", password: "" });
  const [show, setShow] = useState(false);
  return (
    <div className="ar__modal-scrim" onClick={onCancel}>
      <div className="acard ar__modal" role="dialog" aria-modal="true" aria-label={`Edit ${a.name}`} onClick={(e) => e.stopPropagation()}>
        <div className="ar__edit-head">
          <p className="ar__edit-title">Edit {a.name}</p>
          <button type="button" className="acr__icon-btn" aria-label="Close" onClick={onCancel}><HiOutlineXMark/></button>
        </div>
        <div className="ar__edit-body">
          <div className="ar__form-field"><label className="ar__form-label" htmlFor="es-name">Name</label>
            <input id="es-name" className="ar__form-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div className="ar__form-field"><label className="ar__form-label" htmlFor="es-email">Email (they sign in with this)</label>
            <input id="es-email" type="email" className="ar__form-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div className="ar__form-field"><label className="ar__form-label" htmlFor="es-pw">New password (optional)</label>
            <div style={{ display: "flex", gap: "0.4rem" }}>
              <input id="es-pw" type={show ? "text" : "password"} className="ar__form-input" autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Leave empty to keep theirs" />
              <button type="button" className="a-btn a-btn--ghost" aria-label={show ? "Hide password" : "Show password"} onClick={() => setShow(!show)}>{show ? <HiOutlineEyeSlash/> : <HiOutlineEye/>}</button>
            </div>
            <p className="ar__form-hint">At least 8 characters. Setting one signs them out everywhere.</p></div>
        </div>
        <div className="ar__edit-footer">
          <button type="button" className="a-btn a-btn--outline" onClick={onCancel}>Cancel</button>
          <button type="button" className="a-btn a-btn--rust" disabled={!f.name.trim() || !f.email.trim() || (f.password && f.password.length < 8)}
            onClick={() => onSave(f.password ? f : { name: f.name, email: f.email })}><HiOutlineCheck/> Save</button>
        </div>
      </div>
    </div>
  );
}
